import { useCallback, useEffect, useState } from 'react';

/** The non-standard event Chromium fires when the app is installable. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

/**
 * Captures the browser's `beforeinstallprompt` event so the app can offer its
 * own "Install app" button (instead of relying only on the address-bar icon).
 *
 * - `canInstall` is true once the browser signals installability and the app
 *   isn't already installed / running standalone.
 * - `promptInstall()` shows the native install dialog.
 *
 * No-op on browsers that don't support it (Safari/Firefox) — `canInstall`
 * simply stays false, so the button never renders.
 */
export function usePwaInstall() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Already launched as an installed app → never offer install.
    if (window.matchMedia?.('(display-mode: standalone)').matches) {
      setInstalled(true);
    }

    const onBeforeInstallPrompt = (event: Event) => {
      // Stop Chrome's mini-infobar so we control when/where to prompt.
      event.preventDefault();
      setDeferred(event as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferred) return;
    await deferred.prompt();
    try {
      await deferred.userChoice;
    } catch {
      /* ignore */
    }
    // A deferred prompt can only be used once.
    setDeferred(null);
  }, [deferred]);

  return { canInstall: Boolean(deferred) && !installed, installed, promptInstall };
}
